import { defaults, isEqual, isNil, merge, negate } from 'lodash-es';

import { services } from 'services/base';
import {
  ConvertNameSpaceToKeyService,
  CreateStorageWithNameSpaceService,
  GetStorageDataService,
  GetStorageItemService,
  GetStorageKeyService,
  GetStorageUpdateIntervalService,
  NameSpace,
  OnDataChangeServiceCallback,
  OnStorageDataChangeService,
  ReconfigureStorageService,
  SetStorageItemService,
  SetStorateItemServiceOptions,
  StorageApi,
  StorageConfig,
  StorageFns,
  StorageState,
} from 'types';
import { getMainConfig, logger } from 'utils';

const DEFAULT_STORAGE_KEY = 'appStorage';
const PREFIX = 'appFrame.storage';
const ACTION_STORE_DATA_CHANGE = 'AppFrame:Storage:DataChange';
let intervalId: NodeJS.Timeout | null = null;

const getActionName = ({ storageKey }: StorageState) =>
  `${ACTION_STORE_DATA_CHANGE}${storageKey ? `:${storageKey}` : ''}`;

const getLocalStorageData = (storageKey: string) => {
  const localData = localStorage.getItem(storageKey);
  return localData ? JSON.parse(localData) : null;
};

const getStorageState = (options?: Partial<StorageConfig>, defaultStorageKey = DEFAULT_STORAGE_KEY): StorageState => ({
  storageKey: options?.storageKey ?? defaultStorageKey,
  storageData: options?.storageData ?? getLocalStorageData(options?.storageKey ?? defaultStorageKey) ?? {},
  updateInterval: options?.updateInterval,
});

const getStorageApi = (options?: Partial<StorageConfig>): StorageApi => ({
  loadApi: options?.loadApi,
  saveApi: options?.saveApi,
});

const convertNameSpaceToKey = (nameSpace: NameSpace) => {
  return [
    nameSpace.appInstance,
    nameSpace.appPart,
    [nameSpace.name, nameSpace.userId].filter(negate(isNil)).join(':'),
    nameSpace.specifier,
  ]
    .filter(negate(isNil))
    .join('_');
};

const createStorage = async (options?: StorageConfig): Promise<StorageFns> => {
  const { registerAction, clearAction, executeAction } = services;
  const storageState: StorageState = getStorageState(options);
  const storageApi: StorageApi = getStorageApi(options);

  const onDataChange = (callback: OnDataChangeServiceCallback) => {
    registerAction(getActionName(storageState), callback, { order: 1 });
    return () => clearAction(getActionName(storageState), callback);
  };

  const setItem = async (key: string | NameSpace, value: any, options?: SetStorateItemServiceOptions) => {
    const { saveApi } = storageApi;
    const keyStr = typeof key === 'string' ? key : convertNameSpaceToKey(key);
    if (isEqual(storageState.storageData?.[keyStr], value) && !options?.force) {
      return;
    }

    try {
      await saveApi?.(keyStr, value);
    } catch (error) {
      logger.error(`Unable to save storage key '${key}!', ${error}`);
    }
    executeAction(getActionName(storageState), { ...storageState.storageData, [keyStr]: value });
  };

  const getItem = (key: string | NameSpace) => {
    const keyStr = typeof key === 'string' ? key : convertNameSpaceToKey(key);
    return storageState.storageData?.[keyStr] ?? null;
  };

  const getStorageKey = () => storageState.storageKey;
  const getStorageData = () => storageState.storageData;
  const getUpdateInterval = () => storageState.updateInterval;

  const reconfigure = async (newOptions: Partial<StorageConfig>): Promise<any> => {
    // clearAction with old action name is needed (which is storeDataChangeAction here)
    services.clearAction(getActionName(storageState), onDataChange);

    merge(storageState, getStorageState(newOptions, storageState.storageKey));
    merge(storageApi, getStorageApi(newOptions));
    const { updateInterval, storageKey } = storageState;

    // registerAction with new action name
    services.registerAction(
      getActionName(storageState),
      (storageData: object) => {
        storageState.storageData = storageData;
        localStorage.setItem(storageKey, JSON.stringify(storageData));
      },
      { order: 0 },
    );

    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    const { loadApi } = storageApi || {};
    if (loadApi) {
      const loadData = async (refresh: boolean) => {
        const loadedData = await loadApi();
        if (refresh && (!loadedData || isEqual(storageState.storageData, loadedData))) {
          return;
        }
        executeAction(getActionName(storageState), loadedData);
      };

      await loadData(false);

      if (updateInterval) {
        intervalId = setInterval(async () => {
          await loadData(true);
        }, updateInterval);
      }
    }
  };

  await reconfigure(options || {});

  return {
    reconfigure,
    setItem,
    getItem,
    getStorageData,
    getStorageKey,
    onDataChange,
    getUpdateInterval,
  };
};

const DEFAULT_APPFRAME_STORAGE_CONFIG = {
  storageKey: 'AppFrame.Storage',
  updateInterval: 60 * 1000,
};

const initStorageServices = async () => {
  const { registerService } = services;

  const storageConfig = defaults({}, getMainConfig('storage'), DEFAULT_APPFRAME_STORAGE_CONFIG);
  const storage = await createStorage(storageConfig);
  const { reconfigure, setItem, getItem, getStorageData, onDataChange, getStorageKey, getUpdateInterval } = storage;

  registerService<OnStorageDataChangeService>(`${PREFIX}.onDataChange`, onDataChange);
  registerService<SetStorageItemService>(`${PREFIX}.setItem`, setItem);
  registerService<ReconfigureStorageService>(`${PREFIX}.reconfigure`, reconfigure);
  registerService<GetStorageItemService>(`${PREFIX}.getItem`, getItem);
  registerService<GetStorageDataService>(`${PREFIX}.getStorageData`, getStorageData);
  registerService<GetStorageUpdateIntervalService>(`${PREFIX}.getUpdateInterval`, getUpdateInterval);
  registerService<GetStorageKeyService>(`${PREFIX}.getStorageKey`, getStorageKey);
  registerService<CreateStorageWithNameSpaceService>(`${PREFIX}.createStorageWithNameSpace`, createStorage);
  registerService<ConvertNameSpaceToKeyService>(`${PREFIX}.convertNameSpaceToKey`, convertNameSpaceToKey);
};
export default initStorageServices;
